import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { staggerContainer, staggerItem } from '../../lib/animations';
import { getApiUrl } from '../../lib/api';
import AnimatedButton from '../AnimatedButton';
import type { CountryProps } from '../../pages/Cannabis';

/* ── Types ── */
interface Strain {
  id?: number;
  name: string;
  type?: string;
  thc_min?: number;
  thc_max?: number;
  cbd_min?: number;
  cbd_max?: number;
  effects?: string[];
  flavors?: string[];
  description?: string;
  medical_uses?: string[];
  negatives?: string[];
  image_url?: string;
  origin?: string;
  growing_info?: string;
}

/* ── Helpers ── */
function typeBadgeColor(type?: string | null | any) {
  if (typeof type !== 'string') return 'bg-white/10 text-white/60 border-white/20';
  switch (type.toLowerCase()) {
    case 'sativa': return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
    case 'indica': return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
    case 'hybrid': return 'bg-green-500/20 text-green-300 border-green-500/30';
    default: return 'bg-white/10 text-white/60 border-white/20';
  }
}

function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse bg-white/5 rounded-xl ${className}`} />;
}

const TYPE_FILTERS = ['All', 'Sativa', 'Indica', 'Hybrid'] as const;

export default function StrainsTab(_props: CountryProps) {
  const [strains, setStrains] = useState<Strain[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('All');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  const fetchStrains = useCallback(async (pageNum: number, append: boolean) => {
    append ? setLoadingMore(true) : setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(pageNum),
        per_page: '50',
      });
      if (typeFilter !== 'All') params.set('type', typeFilter);
      if (search.trim()) params.set('search', search.trim());

      const res = await fetch(getApiUrl(`/api/strains-data?${params}`));
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      const items: Strain[] = data.items ?? data.strains ?? data.data ?? (Array.isArray(data) ? data : []);
      const total = data.total ?? data.total_count ?? 0;

      setTotalCount(total);
      setStrains(prev => append ? [...prev, ...items] : items);
      setHasMore(items.length >= 50);
    } catch {
      if (!append) setStrains([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [search, typeFilter]);

  // Reset and fetch when filters change
  useEffect(() => {
    setPage(1);
    setExpandedId(null);
    fetchStrains(1, false);
  }, [fetchStrains]);

  function loadMore() {
    const next = page + 1;
    setPage(next);
    fetchStrains(next, true);
  }

  const strainKey = (s: Strain, i: number) => s.id ? String(s.id) : `${s.name}-${i}`;

  const handleImageError = (strainName: string) => {
    setFailedImages(prev => new Set([...prev, strainName]));
  };

  return (
    <div className="space-y-6">
      {/* Search + Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search strains..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#4ade80]/40 transition-colors"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {TYPE_FILTERS.map(t => (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              className={`px-4 py-2 text-xs font-semibold rounded-xl border transition-colors ${
                typeFilter === t
                  ? 'bg-[#4ade80]/20 border-[#4ade80]/40 text-[#4ade80]'
                  : 'bg-white/5 border-white/10 text-white/50 hover:border-white/20'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Result Count */}
      {!loading && (
        <p className="text-white/40 text-sm">
          {totalCount > 0 ? `${totalCount.toLocaleString()} strains found` : `${strains.length} strains`}
        </p>
      )}

      {/* Loading Skeleton */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-64" />)}
        </div>
      ) : strains.length === 0 ? (
        <div className="text-center py-16 text-white/40">
          <p className="text-4xl mb-3">◇</p>
          <p>No strains found. Try a different search or filter.</p>
        </div>
      ) : (
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
        >
          {strains.map((s, i) => {
            const key = strainKey(s, i);
            const isExpanded = expandedId === key;
            return (
              <motion.div key={key} variants={staggerItem} layout>
                <div
                  onClick={() => setExpandedId(isExpanded ? null : key)}
                  className={`cursor-pointer rounded-2xl border bg-white/[0.02] p-4 transition-colors ${
                    isExpanded ? 'border-[#4ade80]/40 bg-white/[0.04]' : 'border-white/10 hover:border-white/20'
                  }`}
                >
                  {!failedImages.has(s.name) && s.image_url ? (
                    <img
                      src={s.image_url}
                      alt={s.name}
                      className="w-full h-36 object-cover rounded-xl mb-3"
                      onError={() => handleImageError(s.name)}
                    />
                  ) : (
                    <div className="w-full h-36 rounded-xl mb-3 bg-gradient-to-br from-[#4ade80]/20 to-green-900/20 border border-green-500/20 flex items-center justify-center">
                      <span className="text-3xl">◇</span>
                    </div>
                  )}
                  <div className="space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-white font-semibold text-sm">{s.name}</h3>
                      {s.type && (
                        <span className={`shrink-0 px-2 py-0.5 text-[10px] font-semibold rounded-full border ${typeBadgeColor(s.type)}`}>
                          {s.type}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-3 text-xs">
                      {(s.thc_min != null || s.thc_max != null) && (
                        <span className="text-[#4ade80]">{s.thc_min ?? '?'}-{s.thc_max ?? '?'}% THC</span>
                      )}
                      {(s.cbd_min != null || s.cbd_max != null) && (
                        <span className="text-blue-400">{s.cbd_min ?? '?'}-{s.cbd_max ?? '?'}% CBD</span>
                      )}
                    </div>
                    {s.effects && s.effects.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {s.effects.slice(0, 3).map(e => (
                          <span key={e} className="px-1.5 py-0.5 text-[10px] rounded-full bg-[#4ade80]/10 text-[#4ade80]/80">
                            {e}
                          </span>
                        ))}
                        {s.effects.length > 3 && (
                          <span className="text-[10px] text-white/30">+{s.effects.length - 3}</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Expanded detail panel */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
                          {s.description && (
                            <p className="text-white/60 text-xs leading-relaxed">{s.description}</p>
                          )}
                          {s.flavors && s.flavors.length > 0 && (
                            <div>
                              <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">Flavors</p>
                              <div className="flex flex-wrap gap-1">
                                {s.flavors.map(f => (
                                  <span key={f} className="px-1.5 py-0.5 text-[10px] rounded-full bg-[#d4a574]/10 text-[#d4a574]">
                                    {f}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {s.medical_uses && s.medical_uses.length > 0 && (
                            <div>
                              <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">Medical Uses</p>
                              <div className="flex flex-wrap gap-1">
                                {s.medical_uses.map(m => (
                                  <span key={m} className="px-1.5 py-0.5 text-[10px] rounded-full bg-blue-500/10 text-blue-300">
                                    {m}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {s.negatives && s.negatives.length > 0 && (
                            <div>
                              <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">Negatives</p>
                              <div className="flex flex-wrap gap-1">
                                {s.negatives.map(n => (
                                  <span key={n} className="px-1.5 py-0.5 text-[10px] rounded-full bg-red-500/10 text-red-300">
                                    {n}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {s.origin && (
                            <p className="text-white/40 text-xs">Origin: <span className="text-white/60">{s.origin}</span></p>
                          )}
                          {s.growing_info && (
                            <p className="text-white/40 text-xs">Growing: <span className="text-white/60">{s.growing_info}</span></p>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      )}

      {/* Load More */}
      {!loading && hasMore && strains.length > 0 && (
        <div className="flex justify-center pt-4">
          <AnimatedButton onClick={loadMore} loading={loadingMore} variant="secondary">
            Load More
          </AnimatedButton>
        </div>
      )}
    </div>
  );
}
